<template>
  <div v-if="chartData && !chartData.withoutFaults" class="PieApexChart">
    <apexchart type="pie" :options="chartData.chartOptions" :series="chartData.values" height="488px"></apexchart>
  </div>

</template>

<script>
import VueApexCharts from 'vue-apexcharts'
import i18n from '@/lib/i18n'

const scaleNumber = (num, inMin, inMax, outMin, outMax) => {
  return (num - inMin) * (outMax - outMin) / (inMax - inMin) + outMin
}

const parseIrrigationData = (csvData) => {
  let csvLines = csvData.split('\n') // CSV Input
  let line
  const auxArrayValues = []
  const arrayValues = []

  for (let j = 1; j < csvLines.length; j++) {
    line = csvLines[j]
    line = line.replace(/\s+/g, '')
    if (line.length > 0) {
      let val = line.split(';')[1]
      if (val) {
        auxArrayValues.push(val.replace(',', '.'))
      }
    }
  }

  // Average each 2 values
  for (let i = 0; i < auxArrayValues.length; i += 2) {
    let val = parseFloat(auxArrayValues[i])
    if (val !== 0.0 && auxArrayValues[i + 1]) {
      let aux = parseFloat(auxArrayValues[i + 1])
      val = (aux === 0.0) ? aux : val + aux
    }
    arrayValues.push(val / 2)
  }

  return arrayValues
}
const getIrrigationChartData = (arrayValues) => {
  const series = []
  const labels = []
  let colors = []
  const FRACTIONS = arrayValues.length

  let minValue = Number.MAX_VALUE
  arrayValues.forEach(val => {
    if (val !== 0 && val < minValue) {
      minValue = val
    }
  })
  const maxValue = Math.max(...arrayValues)

  // Series
  for (let i = 0; i < FRACTIONS; i++) {
    series.push(360 / FRACTIONS)
  }
  // Labels
  for (let i = 0; i < FRACTIONS; i++) {
    labels.push(arrayValues[i].toFixed(1))
  }
  // Colors
  colors = arrayValues.map(val => {
    if (!val) {
      return '#0098E100'
    }

    // If the min and max value are the same, return a medium opacity of 167 or 'A7' in hex
    let opacity = minValue !== maxValue ? Math.floor(scaleNumber(val, minValue, maxValue, 80, 255)).toString(16) : 'A7'
    if (opacity.length === 1) {
      opacity = '0' + opacity
    }
    return '#0098E1' + opacity
  })

  return {
    series,
    labels,
    colors,
    unit: i18n.t('PieChart.mmUnit')
  }
}
const getFaultsChartData = (arrayFaults, selFault) => {
  const MIN_SIZE = 10
  const faultsAngles = arrayFaults.map((fault) => {
    const result = [
      parseInt(fault.from_angle),
      parseInt(fault.to_angle)
    ]
    result.push(`${result[0]}°-${result[1]}°`)
    const diff = result[1] - result[0]
    if (diff < MIN_SIZE) {
      result[1] += MIN_SIZE - diff
    }
    return result
  }).sort((a, b) => {
    if (a[0] < b[0]) {
      return -1
    }
    return 0
  })

  const series = []
  const labels = []
  let degrees = 0
  for (let i = 0; i < faultsAngles.length; i++) {
    let min = faultsAngles[i][0]
    let max = faultsAngles[i][1]
    let label = `${faultsAngles[i][2]}`
    for (let j = 0; j < faultsAngles.length; j++) {
      if (j !== i) {
        if (faultsAngles[j][0] >= min && faultsAngles[j][0] < max) { // If its min is inside actual range
          label += `<br />${faultsAngles[j][2]}`
          max = (faultsAngles[j][1] > max) ? faultsAngles[j][1] : max
          faultsAngles.splice(j--, 1)
        } else if (faultsAngles[j][1] > min && faultsAngles[j][1] <= max) { // If its max is inside actual range
          label += `<br />${faultsAngles[j][2]}`
          min = (faultsAngles[j][0] < min) ? faultsAngles[j][0] : min
          faultsAngles.splice(j--, 1)
        } else if (faultsAngles[j][0] < min && faultsAngles[j][1] > max) { // If it wraps actual range
          label += `<br />${faultsAngles[j][2]}`
          min = faultsAngles[j][0]
          max = faultsAngles[j][1]
          faultsAngles.splice(j--, 1)
        }
      }
    }

    if (degrees < min) {
      series.push(min - degrees)
      labels.push('')
    }
    series.push(max - min)
    labels.push(label)

    degrees = max
  }

  if (degrees < 360) {
    series.push(360 - degrees)
    labels.push('')
  }

  let colors = labels.map(label => {
    let color = '#FFFFFF00'
    if (label) {
      if (selFault && label.includes(selFault.from_angle) && label.includes(selFault.to_angle)) {
        color = '#F32556'
      } else {
        color = '#F96C6B'
      }
    }
    return color
  })

  return {
    series,
    labels,
    colors,
    toColors: colors.map(col => col === '#FFF' ? '#FFF' : '')
  }
}
const getChartOptions = (chartData, selectedKey, emitData) => {
  return {
    chartOptions: {
      chart: {
        events: {
          dataPointSelection (event, chartContext, { dataPointIndex }) {
            const data = []
            switch (selectedKey) {
              case 'faults': {
                let auxAngles = chartData.labels[dataPointIndex]
                let anglesLabel = `${i18n.t('PieChart.angles')}:`
                let brIndex = auxAngles.search('<br />')
                while (brIndex !== -1) {
                  anglesLabel += '<br />&nbsp;'
                  auxAngles = auxAngles.slice(brIndex + 6, auxAngles.length)
                  brIndex = auxAngles.search('<br />')
                }
                data.push([anglesLabel, chartData.labels[dataPointIndex]])

                emitData(data)
                break
              }
              default: {
                const getAngle = (i) => i * 360 / chartData.labels.length
                const auxAngle = `${getAngle(dataPointIndex).toFixed(0)}° a<br/>${getAngle(dataPointIndex + 1).toFixed(0)}°`
                data.push([`${i18n.t('PieChart.angle')}:<br />&nbsp;`, auxAngle])

                data.push([`${i18n.t('PieChart.applicationRate')}:`, `${chartData.labels[dataPointIndex]} mm`])
                emitData(data)
                break
              }
            }
          }
        }
      },
      legend: {
        show: false
      },
      colors: chartData.colors,
      labels: chartData.labels,
      dataLabels: {
        enabled: false
      },
      fill: {
        type: selectedKey === 'faults' ? 'gradient' : 'solid',
        gradient: {
          gradientToColors: chartData.toColors,
          shadeIntensity: 1,
          opacityFrom: 0.7,
          opacityTo: 0.9,
          stops: [0, 90, 100]
        }
      },
      stroke: {
        width: 0
      },
      plotOptions: {
        pie: {
          expandOnClick: false
        }
      },
      tooltip: {
        y: {
          formatter () {
            return (chartData.unit) ? chartData.unit : ''
          },
          title: {
            formatter (seriesName) {
              return seriesName
            }
          }
        }
      }
    },
    values: chartData.series
  }
}

export default {
  props: ['optKey', 'circularData', 'selectedFault', 'selectedIOReport', 'subOptions', 'hideAngles'],
  components: {
    apexchart: VueApexCharts
  },
  methods: {
    emitData (data) {
      this.$emit('onClickSection', data)
    }
  },
  computed: {
    chartData () {
      switch (this.optKey) {
        case 'faults': {
          if (!this.circularData.faults) {
            return null
          } else if (this.circularData.faults.length === 0) {
            return {withoutFaults: true}
          }
          return getChartOptions(getFaultsChartData(this.circularData.faults, this.selectedFault), this.optKey, this.emitData)
        }
        case 'io_report': {
          let ioValues = this.selectedIOReport ? this.selectedIOReport.csvData : this.circularData.io_report_data
          if (!ioValues) {
            return null
          }
          return getChartOptions(getIrrigationChartData(ioValues), this.optKey, this.emitData)
        }
        default: {
          if (!this.circularData.homogeneity_data) {
            return null
          }
          return getChartOptions(getIrrigationChartData(parseIrrigationData(this.circularData.homogeneity_data)), this.optKey, this.emitData)
        }
      }
    }
  }
}
</script>


// WEBPACK FOOTER //
// PieChart.vue?01ec4e80